/**
 * Keeps the free project, "Livre", usable (docs/refactor/49): asks for it when the organization has no repository at
 * all, and on the desktop gives it a folder of its own — no dialog — and has this computer take its commands. Renders
 * nothing; mounted once in the shell.
 */

import { useEffect, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import { isDesktop } from '../lib/desktop'
import { freeProjectStep } from '../lib/freeProject'
import { folderFor, rememberFolder } from '../lib/repoFolders'
import { readRunnerSettings, saveRunnerSettings } from '../lib/runnerHost'

export function FreeProject() {
  const queryClient = useQueryClient()
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.auth.me, staleTime: 5 * 60_000 })
  const { data: repositories } = useQuery({ queryKey: ['repositories'], queryFn: api.repositories.list, enabled: Boolean(me?.developerId), retry: false })
  // Once per step and value: a failure is not retried in a loop.
  const tried = useRef(new Set<string>())

  useEffect(() => {
    const step = freeProjectStep(repositories, folderFor, isDesktop())
    if (!step) return
    const key = step.kind === 'folder' ? `folder:${step.name}` : 'ensure'
    if (tried.current.has(key)) return
    tried.current.add(key)
    if (step.kind === 'ensure') {
      void api.repositories.ensureDefault().then(() => queryClient.invalidateQueries({ queryKey: ['repositories'] })).catch(() => {})
      return
    }
    void (async () => {
      const { invoke } = await import('@tauri-apps/api/core')
      const path = await invoke<string>('free_project_folder', { name: step.name })
      rememberFolder(step.name, path)
      // This computer takes the free project's commands too, like the projects opened here.
      const settings = readRunnerSettings()
      if (!settings.repositories.includes(step.name)) saveRunnerSettings({ ...settings, repositories: [...settings.repositories, step.name] })
      void queryClient.invalidateQueries({ queryKey: ['repositories'] })
    })().catch(() => {})
  }, [repositories, queryClient])

  return null
}
